import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid, LayoutDashboard, Presentation, Table2, BookOpen, Share2, Users, LogOut, MessagesSquare,
  FileBarChart, BellRing, Radio, Network, Settings2, Command, ScanSearch,
  ChevronDown, Activity, Boxes, ShieldCheck, Menu, ClipboardList, FileCheck2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger, SheetClose, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { NotificationBell } from "@/components/alerts/NotificationBell";
import { TrialBanner } from "@/components/TrialBanner";
import { WORKSPACE, SETTINGS, CHECKS } from "@/constants/testIds";
import { useSession } from "@/context/AuthContext";
import { SemaLogo } from "@/components/marketing/SemaLogo";

// Primary links rendered inline on desktop.
const PRIMARY = [
  { to: "/dashboard", label: "Overview", icon: LayoutGrid, testid: WORKSPACE.navDashboard },
  { to: "/chat", label: "Ask", icon: MessagesSquare, testid: WORKSPACE.navChat },
  { to: "/ask", label: "Explain", icon: ScanSearch, testid: WORKSPACE.navAsk },
  { to: "/boards", label: "Boards", icon: LayoutDashboard, testid: WORKSPACE.navBoards },
  { to: "/present", label: "Present", icon: Presentation, testid: WORKSPACE.navPresent },
];

// Secondary links grouped into dropdowns to keep the bar within bounds.
const GROUPS = [
  {
    key: "monitor", label: "Monitor", icon: Activity, testid: WORKSPACE.navMonitor,
    items: [
      { to: "/reports", label: "Reports", icon: FileBarChart, testid: WORKSPACE.navReports },
      { to: "/alerts", label: "Alerts", icon: BellRing, testid: WORKSPACE.navAlerts },
      { to: "/signals/queue", label: "Signals", icon: Radio, testid: WORKSPACE.navSignals, match: "/signals" },
    ],
  },
  {
    key: "govern", label: "Govern", icon: ShieldCheck, testid: CHECKS.navGovernance,
    items: [
      { to: "/checks", label: "Checks", icon: FileCheck2, testid: CHECKS.navChecks },
      { to: "/survey-readiness", label: "Audit", icon: ClipboardList, testid: CHECKS.navSurvey },
    ],
  },
  {
    key: "model", label: "Model", icon: Boxes, testid: WORKSPACE.navModel,
    items: [
      { to: "/schema", label: "Schema", icon: Table2, testid: WORKSPACE.navSchema },
      { to: "/glossary", label: "Glossary", icon: BookOpen, testid: WORKSPACE.navGlossary },
      { to: "/semantics", label: "Semantics", icon: Network, testid: WORKSPACE.navSemantics, match: "/semantics" },
      { to: "/graph", label: "Graph", icon: Share2, testid: WORKSPACE.navGraph },
    ],
  },
  {
    key: "admin", label: "Admin", icon: ShieldCheck, testid: WORKSPACE.navAdmin,
    items: [
      { to: "/team", label: "Team", icon: Users, testid: WORKSPACE.navTeam },
      { to: "/settings/workspace", label: "Settings", icon: Settings2, testid: SETTINGS.navSettings, match: "/settings" },
    ],
  },
];

const ALL_LINKS = [...PRIMARY, ...GROUPS.flatMap((g) => g.items)];

const linkBase =
  "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1.5 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors";

export function WorkspaceNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useSession();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (link) =>
    link.match ? location.pathname.startsWith(link.match) : location.pathname === link.to;

  const groupActive = (group) => group.items.some(isActive);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[88rem] items-center justify-between gap-3 px-4 py-3 sm:px-6">
          {/* Left: brand + switcher + desktop nav */}
          <div className="flex min-w-0 items-center gap-2 overflow-hidden xl:gap-3">
            <SemaLogo to="/dashboard" imgClassName="h-7" testid="workspace-nav-logo" className="shrink-0" />
            <WorkspaceSwitcher />

            <nav className="hidden items-center gap-0.5 min-[1360px]:flex">
              {PRIMARY.map((link) => {
                const Icon = link.icon;
                const active = isActive(link);
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    data-testid={link.testid}
                    className={`${linkBase} ${
                      active ? "bg-accent/10 text-accent" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {link.label}
                  </Link>
                );
              })}

              {GROUPS.map((group) => {
                const Icon = group.icon;
                const active = groupActive(group);
                return (
                  <DropdownMenu key={group.key}>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={group.testid}
                        className={`${linkBase} outline-none ${
                          active ? "bg-accent/10 text-accent" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {group.label}
                        <ChevronDown className="h-3 w-3 opacity-60" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[11rem] font-mono">
                      {group.items.map((item) => {
                        const ItemIcon = item.icon;
                        const itemActive = isActive(item);
                        return (
                          <DropdownMenuItem key={item.to} asChild>
                            <Link
                              to={item.to}
                              data-testid={item.testid}
                              className={`cursor-pointer text-xs uppercase tracking-[0.12em] ${
                                itemActive ? "text-accent" : ""
                              }`}
                            >
                              <ItemIcon className="h-3.5 w-3.5" />
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}
            </nav>
          </div>

          {/* Right: actions */}
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <button
              data-testid={WORKSPACE.cmdkTrigger}
              onClick={() => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
              title="Command palette (⌘K)"
              className="hidden items-center gap-2 rounded-full border border-border/70 px-3 py-1.5 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground 2xl:inline-flex"
            >
              <Command className="h-3.5 w-3.5" />
              <span className="tracking-[0.12em]">K</span>
            </button>
            <NotificationBell />
            <ThemeToggle />
            <Button
              variant="outline"
              size="sm"
              data-testid={WORKSPACE.navLogout}
              onClick={onLogout}
              title="Sign out"
              aria-label="Sign out"
              className="hidden rounded-full border-border/80 px-2.5 sm:inline-flex"
            >
              <LogOut className="h-3.5 w-3.5" />
            </Button>

            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid={WORKSPACE.mobileMenu}
                  className="inline-flex items-center justify-center rounded-full border border-border/70 p-2 text-muted-foreground transition-colors hover:text-foreground min-[1360px]:hidden"
                  aria-label="Open menu"
                >
                  <Menu className="h-4 w-4" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 font-mono">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <SemaLogo to={null} imgClassName="h-7" testid="workspace-nav-mobile-logo" />
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-6 flex flex-col gap-1">
                  {ALL_LINKS.map((link) => {
                    const Icon = link.icon;
                    const active = isActive(link);
                    return (
                      <SheetClose asChild key={link.to}>
                        <Link
                          to={link.to}
                          data-testid={`m-${link.testid}`}
                          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-xs uppercase tracking-[0.14em] transition-colors ${
                            active ? "bg-accent/10 text-accent" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {link.label}
                        </Link>
                      </SheetClose>
                    );
                  })}
                  <button
                    onClick={() => { setMobileOpen(false); onLogout(); }}
                    className="mt-3 flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-xs uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <TrialBanner />
    </>
  );
}
